export type UsageLimitContext =
  | 'ask_source'
  | 'source_summary'
  | 'create_learning'
  | 'general';

export interface LuminaApiError extends Error {
  status?: number;
  code?: string | null;
  payload?: any;
  isUsageLimitError?: boolean;
  isPlanAccessError?: boolean;
}

export interface UsageLimitNotice {
  title: string;
  message: string;
  action: string;
  scope: 'user' | 'organisation' | 'unknown';
}

const USER_LIMIT_CODES = new Set([
  'USER_DAILY_USAGE_LIMIT_REACHED'
]);

const ORG_LIMIT_CODES = new Set([
  'ORG_DAILY_USAGE_LIMIT_REACHED'
]);

const USER_PLAN_CODES = new Set([
  'USER_PLAN_SUSPENDED',
  'USER_PLAN_EXPIRED',
  'USER_ACCOUNT_NOT_FOUND'
]);

const ORGANISATION_PLAN_CODES = new Set([
  'ORGANISATION_PLAN_SUSPENDED',
  'ORGANISATION_PLAN_EXPIRED',
  'ORGANISATION_NOT_FOUND',
  'ORGANISATION_MEMBERSHIP_REQUIRED'
]);

const PLAN_CHECK_CODES = new Set([
  'PLAN_ACCESS_DENIED',
  'PLAN_ACCESS_CHECK_FAILED',
  'USAGE_LIMIT_CHECK_FAILED'
]);

function normaliseText(value: unknown): string {
  return String(value || '').trim();
}

function getErrorCode(error: any): string {
  return normaliseText(
    error?.code ||
    error?.errorCode ||
    error?.payload?.code ||
    error?.payload?.errorCode ||
    error?.payload?.data?.code
  );
}

function getErrorMessage(error: any): string {
  return normaliseText(
    error?.message ||
    error?.payload?.error ||
    error?.payload?.message ||
    error
  );
}

function getContextLabel(context: UsageLimitContext): string {
  switch (context) {
    case 'ask_source':
      return 'Ask Source';
    case 'source_summary':
      return 'Generate Summary';
    case 'create_learning':
      return 'Create Learning';
    default:
      return 'AI action';
  }
}

function detectScope(error: any): 'user' | 'organisation' | 'unknown' {
  const code = getErrorCode(error);
  const message = getErrorMessage(error).toLowerCase();

  if (ORG_LIMIT_CODES.has(code) || ORGANISATION_PLAN_CODES.has(code) || message.includes('organisation') || message.includes('organization')) {
    return 'organisation';
  }

  if (USER_LIMIT_CODES.has(code) || USER_PLAN_CODES.has(code) || message.includes('you have reached') || message.includes('your generation limit')) {
    return 'user';
  }

  return 'unknown';
}

export function isPlanAccessError(error: any): boolean {
  const code = getErrorCode(error);
  const message = getErrorMessage(error).toLowerCase();

  return (
    USER_PLAN_CODES.has(code) ||
    ORGANISATION_PLAN_CODES.has(code) ||
    PLAN_CHECK_CODES.has(code) ||
    message.includes('pilot access has expired') ||
    message.includes('account is suspended') ||
    message.includes('organisation is suspended') ||
    message.includes('verify pilot access') ||
    message.includes('verify today\'s usage')
  );
}

export function isUsageLimitError(error: any): boolean {
  const code = getErrorCode(error);
  const message = getErrorMessage(error).toLowerCase();

  return (
    Number(error?.status) === 429 ||
    USER_LIMIT_CODES.has(code) ||
    ORG_LIMIT_CODES.has(code) ||
    message.includes('usage limit') ||
    message.includes('pilot usage limit') ||
    message.includes('daily generation limit') ||
    message.includes('generation limit has been reached') ||
    message.includes('limit reached') ||
    message.includes('reached today')
  );
}

export function createApiError(response: Response, envelope: any, fallbackMessage: string): LuminaApiError {
  const message = normaliseText(envelope?.error || envelope?.message || fallbackMessage);
  const error = new Error(message || fallbackMessage) as LuminaApiError;

  error.status = response.status;
  error.code = envelope?.code || envelope?.errorCode || envelope?.data?.code || null;
  error.payload = envelope;
  error.isUsageLimitError = isUsageLimitError(error);
  error.isPlanAccessError = isPlanAccessError(error);

  return error;
}

export function getUsageLimitNotice(error: any, context: UsageLimitContext): UsageLimitNotice | null {
  if (!isUsageLimitError(error) && !isPlanAccessError(error)) {
    return null;
  }

  const scope = detectScope(error);
  const label = getContextLabel(context);
  const baseMessage = getErrorMessage(error);
  const code = getErrorCode(error);
  const planAccessError = isPlanAccessError(error);

  const isSuspended = code.includes('SUSPENDED') || baseMessage.toLowerCase().includes('suspended');
  const isExpired = code.includes('EXPIRED') || baseMessage.toLowerCase().includes('expired');

  const title = isSuspended
    ? scope === 'organisation' ? 'Organisation access suspended' : 'Account access suspended'
    : isExpired
      ? scope === 'organisation' ? 'Organisation pilot expired' : 'Pilot access expired'
      : planAccessError
        ? 'Pilot access could not be verified'
        : scope === 'organisation'
          ? 'Organisation pilot limit reached'
          : "Today's pilot usage limit reached";

  const requestLine = `${label} was not sent to the AI model, so no additional AI cost was incurred.`;

  const action = isSuspended
    ? 'Contact the Lumina admin to restore access.'
    : isExpired
      ? 'Contact the Lumina admin to renew pilot access.'
      : planAccessError
        ? 'Try again or contact the Lumina admin if access cannot be verified.'
        : scope === 'organisation'
          ? 'Ask your Lumina admin to increase pilot access for this organisation, or try again tomorrow.'
          : 'Try again tomorrow or contact Lumina admin for extended pilot access.';

  const message = [
    baseMessage || `${label} is temporarily limited during the controlled pilot.`,
    requestLine,
    action
  ].filter(Boolean).join('\n');

  return {
    title,
    message,
    action,
    scope
  };
}

export function getFriendlyErrorMessage(
  error: any,
  context: UsageLimitContext,
  fallbackMessage: string
): string {
  const notice = getUsageLimitNotice(error, context);

  if (notice) {
    return `${notice.title}\n${notice.message}`;
  }

  return getErrorMessage(error) || fallbackMessage;
}

export function getAssistantFailureMessage(error: any, context: UsageLimitContext): string {
  const notice = getUsageLimitNotice(error, context);

  if (notice) {
    return `${notice.title}. ${notice.action} The request was stopped before it reached the AI model.`;
  }

  return 'I could not answer from this Source Set because the request failed. Please try again with a smaller or clearer question.';
}
